import {useState} from 'react';

import {PIN_STATUS} from '/component/base/PinInput/PinInput.constant';
import {
  HAS_OPTED_INTO_BIOMETRICS,
  HAS_OPTED_INTO_BIOMETRICS_AND_PIN,
  HAS_SETUP_PIN,
  HAS_SUCCESSFULLY_SETUP_PIN_AND_BIOMETRICS,
  SHOW_SUCCESS_MODAL,
  USER_PIN,
} from '/enum/storage.enum';
import {useStorage} from '/util/async-storage/useStorage';
import {generateCallback} from '/util/component.util';
import {handlePinOptIn, storeEncryptedPin} from '/util/pinCore.util';

// Define types for props
interface UseCreatePinInputProps {
  createTitle: string;
  confirmTitle: string;
  onClose: () => void;
  onFirstLogin: boolean;
  onSuccess: () => void;
}

// Define return type
interface UseCreatePinInputReturn {
  onInputComplete: () => Promise<void>;
  onInputPin: (currentPin: string) => string;
  getText: () => string;
  pinStatus: string;
}

export const useCreatePinInput = ({
  createTitle,
  confirmTitle,
  // onClose,
  onFirstLogin,
  onSuccess,
}: UseCreatePinInputProps): UseCreatePinInputReturn => {
  const [pin, setPin] = useState<string>('');
  const [pinStatus, setPinStatus] = useState<string>(PIN_STATUS.initial);

  const {setValue: setUserPinValue} = useStorage<boolean>({
    key: USER_PIN,
    defaultValue: false,
  });
  const {setValue: setPinOptInValue} = useStorage<boolean>({
    key: HAS_SETUP_PIN,
    defaultValue: false,
  });
  const {setValue: setInitSuccess} = useStorage<boolean>({
    key: HAS_SUCCESSFULLY_SETUP_PIN_AND_BIOMETRICS,
    defaultValue: false,
  });
  const {setValue: setInitBiometricsAndPin} = useStorage<boolean>({
    key: HAS_OPTED_INTO_BIOMETRICS_AND_PIN,
    defaultValue: false,
  });
  const {setValue: setBiometricsOptInValue} = useStorage<boolean>({
    key: HAS_OPTED_INTO_BIOMETRICS,
    defaultValue: false,
  });
  const {setValue: setSuccessStatus} = useStorage<boolean>({
    key: SHOW_SUCCESS_MODAL,
    defaultValue: false,
  });

  const getText = (): string => {
    return pinStatus === PIN_STATUS.initial ? createTitle : confirmTitle;
  };

  const onInputPin = (currentPin: string): string => {
    let currentStatus = pinStatus;

    if (pinStatus === PIN_STATUS.initial) {
      currentStatus = PIN_STATUS.confirm;
      setPin(currentPin);
    }
    if (
      currentPin === pin &&
      (pinStatus === PIN_STATUS.confirm || pinStatus === PIN_STATUS.error)
    ) {
      currentStatus = PIN_STATUS.enter;
    }
    if (currentPin !== pin && pinStatus === PIN_STATUS.confirm) {
      currentStatus = PIN_STATUS.error;
    }

    setPinStatus(currentStatus);
    return currentStatus;
  };

  const onInputComplete = async (): Promise<void> => {
    if (!onFirstLogin) {
      await storeEncryptedPin({
        pin,
        setUserPinValue,
      })().then(generateCallback({func: setPinOptInValue, params: true}));
      if (onSuccess) {
        onSuccess(); // triggers PinModal to show success modal immediately
      }
    } else {
      await handlePinOptIn({
        pin,
        setUserPinValue,
      })().then(() => {
        setPinOptInValue(true);
        setBiometricsOptInValue(true);
        setInitBiometricsAndPin(false);
        setInitSuccess(true);
        setSuccessStatus(true);
      });
      if (onSuccess) {
        onSuccess(); // triggers PinModal to show success modal immediately
      }
    }
  };

  return {onInputComplete, onInputPin, getText, pinStatus};
};
